/**
 * Helpers for loading Medical Necessity evaluation payloads.
 * @module loadEvaluationPayload
 */
import {
  fetchNccnGuidelinesForOrder,
  getNccnEvaluation,
} from "../../../api/postCall/nccnEvaluation";
import {
  GetNCCNEvaluationResponse,
  LoadedNccnEvaluation,
  NCCNEvaluationPayload,
} from "../../../data-model/nccnEvaluation";
import { FetchNCCNGuidelinesResponse } from "../../../data-model/nccnGuidelines";

/**
 * Maps a live NCCN fetch response into the stored-evaluation payload shape.
 *
 * @param response - The `fetch-nccn-guidelines` pathway-mode response
 * @returns The equivalent {@link NCCNEvaluationPayload}
 */
const toPayloadFromFetch = (
  response: FetchNCCNGuidelinesResponse,
): NCCNEvaluationPayload => ({
  order_id: response.order_id,
  icd_code: response.icd_code,
  j_codes: response.j_codes,
  regimen: response.regimen,
  order_evaluation_result: response.order_evaluation_result,
  notes_path: response.notes_path,
  ai_review_refuted: false,
  guidelines: (response.guidelines ?? []).map((record) => ({
    guideline_id: record.identifier,
    j_code: record.j_code,
    drug_name: record.drug_name,
    drug_brand: record.drug_brand,
    guideline: record.guideline,
    guideline_type: record.guideline_type,
    guideline_name: record.guideline_name,
    regimen: record.regimen,
    metadata: record.metadata,
    decomposition_status: record.decomposition_status,
  })),
});

/**
 * Wraps a live fetch response as {@link LoadedNccnEvaluation}.
 *
 * A fresh agent eval is current, so envelope `order_evaluation_result` matches
 * the payload AI rollup. `review_submitted` comes from the fetch envelope.
 * `ai_evaluation` is left unset: the fetch response has no such field, and the
 * column is only populated once the agent write lands.
 *
 * @param response - The `fetch-nccn-guidelines` pathway-mode response
 * @returns Envelope plus payload
 */
export const toEvaluationPayload = (
  response: FetchNCCNGuidelinesResponse,
): LoadedNccnEvaluation => ({
  payload: toPayloadFromFetch(response),
  review_submitted: response.review_submitted ?? false,
  order_evaluation_result: response.order_evaluation_result,
  ai_review_refuted: false,
});

/**
 * Maps a GET evaluation response into {@link LoadedNccnEvaluation}.
 *
 * Envelope columns are preferred for order-level flags; JSONB is the fallback
 * for un-backfilled rows. `ai_evaluation` has no JSONB counterpart (the stored
 * payload's `order_evaluation_result` is the current result, not the AI's), so
 * it maps to the column alone and stays null when the column is unset.
 *
 * @param saved - The `get-nccn-evaluation` response
 * @returns The loaded evaluation, or null when none was found
 */
export const fromGetResponse = (
  saved: GetNCCNEvaluationResponse,
): LoadedNccnEvaluation | null => {
  if (!saved.found || !saved.response_json) return null;
  const payload = saved.response_json;
  return {
    payload,
    review_submitted:
      saved.review_submitted ?? payload.review_submitted ?? false,
    order_evaluation_result:
      saved.order_evaluation_result ?? payload.order_evaluation_result,
    ai_evaluation: saved.ai_evaluation,
    ai_review_refuted:
      saved.ai_review_refuted ?? payload.ai_review_refuted ?? false,
  };
};

/**
 * Loads a saved evaluation or fetches a fresh agent evaluation for an order.
 *
 * @param orderId - Order ID
 * @param orgId - Optional organisation ID
 * @returns Loaded evaluation (payload + envelope flags)
 */
export const loadEvaluationPayload = async (
  orderId: string,
  orgId?: string,
): Promise<LoadedNccnEvaluation> => {
  const saved = await getNccnEvaluation({
    order_id: orderId,
    org_id: orgId ?? null,
  });
  const fromStore = fromGetResponse(saved);
  if (fromStore) return fromStore;

  const fetched = await fetchNccnGuidelinesForOrder({
    order_id: orderId,
    org_id: orgId ?? null,
    direct_eval: true,
    pathway_mode: true,
  });
  return toEvaluationPayload(fetched);
};

/**
 * Converts an ALL-CAPS (or mixed-case) name to title case.
 *
 * @param value - The raw name to normalise
 * @returns The title-cased name
 */
export const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .replace(
      /(^|[\s-])([a-z])/g,
      (_match, sep, char) => sep + char.toUpperCase(),
    );
